// Parser de ficheiros IDS (buildingSMART Information Delivery
// Specification, versão 1.0). Sem dependências e sem backend.
//
// O XML é lido pelo leitor próprio do pacote (ver xml.ts), para o
// comportamento ser idêntico no browser e em Node (o DOMParser é um
// global do browser e não existe em Node). Quem preferir o parser da
// plataforma pode injetá-lo em `parseIds(xml, { domParser })`.
//
// O IDS descreve, por especificação, um conjunto de elementos a que se
// APLICA (applicability) e os requisitos que esses elementos têm de
// cumprir (requirements). Cada um é uma lista de "facets": entity,
// attribute, property, classification, material, partOf. Os valores
// (nomes, valores de propriedade, ...) são "idsValue": um valor exato
// (simpleValue) ou uma restrição XSD (enumeration, pattern, bounds,
// length).
//
// Tolerância deliberada: um facet ou restrição que não reconhecemos é
// registado (unsupported) em vez de rebentar o parse, para que um IDS
// com features novas continue a validar o que dá.
//
// Referência: XSD e casos de teste em
// https://github.com/buildingSMART/IDS

import {
  attributeBaseType,
  primitiveAttributesOf,
  acceptsPattern,
  type AttrBaseType,
} from './ifcSchemaTypes.js'
import {
  parseXml,
  parseXmlWith,
  XmlParseError,
  type DomParserLike,
  type XmlElementLike,
} from './xml.js'
import { MESSAGES_EN, type IdsMessages } from './idsMessages.js'

/** Elemento XML tal como o parser o vê. Alias local para as funções
 *  abaixo lerem melhor. */
type Element = XmlElementLike

/** Opções de `parseIds`. */
export interface ParseIdsOptions {
  /** Parser XML alternativo (ex.: `new DOMParser()` no browser, ou o do
   *  `@xmldom/xmldom` em Node). Por omissão usa o leitor próprio do
   *  pacote, que se comporta igual em todos os runtimes. */
  domParser?: DomParserLike
  /** Língua dos erros de validação. Por omissão inglês. */
  messages?: IdsMessages
}

/** Cardinalidade de um requisito. Nos requirements do IDS 1.0 vem no
 *  atributo `cardinality`; ausente = 'required'. Na applicability não
 *  se aplica (todos os facets têm de bater). */
export type Cardinality = 'required' | 'prohibited' | 'optional'

/** Restrição sobre um valor (idsValue). Campos ausentes = sem essa
 *  restrição. Objeto vazio = "presente, qualquer valor". */
export interface IdsValue {
  /** Valor exato (simpleValue). */
  simpleValue?: string
  /** xs:enumeration: um de. */
  enumeration?: string[]
  /** xs:pattern: expressão regular (a string do XSD). */
  pattern?: string
  minInclusive?: number
  maxInclusive?: number
  minExclusive?: number
  maxExclusive?: number
  length?: number
  minLength?: number
  maxLength?: number
}

export interface EntityFacet {
  type: 'entity'
  /** Nome da classe IFC (ex.: IFCWALL). Sempre presente. */
  name: IdsValue
  predefinedType?: IdsValue
  cardinality?: Cardinality
  instructions?: string
}

export interface AttributeFacet {
  type: 'attribute'
  name: IdsValue
  value?: IdsValue
  cardinality?: Cardinality
  instructions?: string
}

export interface PropertyFacet {
  type: 'property'
  propertySet: IdsValue
  baseName: IdsValue
  value?: IdsValue
  /** Tipo de dados declarado (ex.: IFCLABEL). */
  dataType?: string
  cardinality?: Cardinality
  instructions?: string
}

export interface ClassificationFacet {
  type: 'classification'
  system?: IdsValue
  value?: IdsValue
  uri?: string
  cardinality?: Cardinality
  instructions?: string
}

export interface MaterialFacet {
  type: 'material'
  value?: IdsValue
  uri?: string
  cardinality?: Cardinality
  instructions?: string
}

export interface PartOfFacet {
  type: 'partOf'
  /** Entidade contentora (o partOf tem um <entity> lá dentro). */
  entity?: EntityFacet
  relation?: string
  cardinality?: Cardinality
  instructions?: string
}

/** Facet que não sabemos avaliar (feature nova ou desconhecida). É
 *  registado para o relatório mostrar "não avaliado" em vez de falhar. */
export interface UnsupportedFacet {
  type: 'unsupported'
  tag: string
  cardinality?: Cardinality
}

export type Facet =
  | EntityFacet
  | AttributeFacet
  | PropertyFacet
  | ClassificationFacet
  | MaterialFacet
  | PartOfFacet
  | UnsupportedFacet

export interface IdsSpec {
  name: string | null
  /** Esquemas IFC a que a spec se aplica (ex.: ['IFC4']). */
  ifcVersion: string[]
  identifier: string | null
  description: string | null
  instructions: string | null
  applicability: Facet[]
  requirements: Facet[]
  /** Cardinalidade ao nível da specification, do minOccurs/maxOccurs no
   *  elemento <applicability>: 'required' (>=1 entidade aplicável, o
   *  default), 'optional' (minOccurs=0), 'prohibited' (maxOccurs=0). */
  applicabilityCardinality: Cardinality
}

export interface IdsInfo {
  title: string | null
  description: string | null
  author: string | null
  date: string | null
  version: string | null
  purpose: string | null
}

export interface IdsFile {
  info: IdsInfo
  specs: IdsSpec[]
}

// --- Helpers de DOM tolerantes a namespace (comparam localName) -------

function kids(el: Element, localName: string): Element[] {
  const out: Element[] = []
  for (let i = 0; i < el.children.length; i++) {
    const c = el.children[i]
    if (c.localName === localName) out.push(c)
  }
  return out
}

function kid(el: Element, localName: string): Element | null {
  for (let i = 0; i < el.children.length; i++) {
    const c = el.children[i]
    if (c.localName === localName) return c
  }
  return null
}

function textOf(el: Element | null): string | null {
  if (!el) return null
  const t = el.textContent
  return t === null ? null : t.trim()
}

function numAttr(el: Element | null): number | undefined {
  if (!el) return undefined
  const v = el.getAttribute('value')
  if (v === null) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** Lê um idsValue de um elemento-campo (ex.: o <name> de um facet).
 *  undefined quando o campo não existe (sem restrição); objeto (mesmo
 *  vazio) quando existe. */
function parseIdsValue(field: Element | null): IdsValue | undefined {
  if (!field) return undefined
  const v: IdsValue = {}
  const simple = kid(field, 'simpleValue')
  if (simple) {
    v.simpleValue = textOf(simple) ?? ''
  }
  const restriction = kid(field, 'restriction')
  if (restriction) {
    const enums = kids(restriction, 'enumeration')
      .map((e) => e.getAttribute('value'))
      .filter((s): s is string => s !== null)
    if (enums.length > 0) v.enumeration = enums
    // Vários <xs:pattern> na mesma restrição = OR (o valor corresponde se
    // bater em qualquer um). Juntamo-los com '|'; cada padrão fica numa
    // alternativa e o matchPattern ancora tudo com ^(?:...)$.
    const pats = kids(restriction, 'pattern')
      .map((p) => p.getAttribute('value'))
      .filter((s): s is string => s !== null)
    if (pats.length === 1) v.pattern = pats[0]
    else if (pats.length > 1) v.pattern = pats.map((p) => `(?:${p})`).join('|')
    v.minInclusive = numAttr(kid(restriction, 'minInclusive'))
    v.maxInclusive = numAttr(kid(restriction, 'maxInclusive'))
    v.minExclusive = numAttr(kid(restriction, 'minExclusive'))
    v.maxExclusive = numAttr(kid(restriction, 'maxExclusive'))
    v.length = numAttr(kid(restriction, 'length'))
    v.minLength = numAttr(kid(restriction, 'minLength'))
    v.maxLength = numAttr(kid(restriction, 'maxLength'))
  }
  return v
}

function cardinalityOf(el: Element): Cardinality | undefined {
  const c = el.getAttribute('cardinality')
  if (c === 'required' || c === 'prohibited' || c === 'optional') return c
  // Ficheiros antigos usavam minOccurs/maxOccurs; mapear o essencial.
  const min = el.getAttribute('minOccurs')
  if (min === '0') return 'optional'
  return undefined
}

function parseFacet(el: Element): Facet {
  const tag = el.localName
  const cardinality = cardinalityOf(el)
  const instructions = el.getAttribute('instructions') ?? undefined
  switch (tag) {
    case 'entity':
      return {
        type: 'entity',
        name: parseIdsValue(kid(el, 'name')) ?? {},
        predefinedType: parseIdsValue(kid(el, 'predefinedType')),
        cardinality,
        instructions,
      }
    case 'attribute':
      return {
        type: 'attribute',
        name: parseIdsValue(kid(el, 'name')) ?? {},
        value: parseIdsValue(kid(el, 'value')),
        cardinality,
        instructions,
      }
    case 'property':
      return {
        type: 'property',
        propertySet: parseIdsValue(kid(el, 'propertySet')) ?? {},
        baseName: parseIdsValue(kid(el, 'baseName')) ?? {},
        value: parseIdsValue(kid(el, 'value')),
        dataType: el.getAttribute('dataType') ?? undefined,
        cardinality,
        instructions,
      }
    case 'classification':
      return {
        type: 'classification',
        system: parseIdsValue(kid(el, 'system')),
        value: parseIdsValue(kid(el, 'value')),
        uri: el.getAttribute('uri') ?? undefined,
        cardinality,
        instructions,
      }
    case 'material':
      return {
        type: 'material',
        value: parseIdsValue(kid(el, 'value')),
        uri: el.getAttribute('uri') ?? undefined,
        cardinality,
        instructions,
      }
    case 'partOf': {
      const entEl = kid(el, 'entity')
      return {
        type: 'partOf',
        entity: entEl ? (parseFacet(entEl) as EntityFacet) : undefined,
        relation: el.getAttribute('relation') ?? undefined,
        cardinality,
        instructions,
      }
    }
    default:
      return { type: 'unsupported', tag, cardinality }
  }
}

function parseFacetList(container: Element | null): Facet[] {
  if (!container) return []
  const out: Facet[] = []
  for (let i = 0; i < container.children.length; i++) {
    out.push(parseFacet(container.children[i]))
  }
  return out
}

/** Cardinalidade ao nível da specification, lida do minOccurs/maxOccurs
 *  do elemento <applicability> (defaults do xs:occurs: minOccurs=1). */
function applicabilityCardinalityOf(app: Element | null): Cardinality {
  if (!app) return 'required'
  if (app.getAttribute('maxOccurs') === '0') return 'prohibited'
  if (app.getAttribute('minOccurs') === '0') return 'optional'
  return 'required'
}

function parseSpec(el: Element): IdsSpec {
  const ifcVersion = (el.getAttribute('ifcVersion') ?? '')
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
  const app = kid(el, 'applicability')
  return {
    name: el.getAttribute('name'),
    ifcVersion,
    identifier: el.getAttribute('identifier'),
    description: el.getAttribute('description'),
    instructions: el.getAttribute('instructions'),
    applicability: parseFacetList(app),
    requirements: parseFacetList(kid(el, 'requirements')),
    applicabilityCardinality: applicabilityCardinalityOf(app),
  }
}

function parseInfo(el: Element | null): IdsInfo {
  return {
    title: textOf(el ? kid(el, 'title') : null),
    description: textOf(el ? kid(el, 'description') : null),
    author: textOf(el ? kid(el, 'author') : null),
    date: textOf(el ? kid(el, 'date') : null),
    version: textOf(el ? kid(el, 'version') : null),
    purpose: textOf(el ? kid(el, 'purpose') : null),
  }
}

/** Tipos de dados IDS cujo valor XSD subjacente é inteiro: o literal não
 *  pode ter parte decimal (ex.: "42." ou "42.0" são inválidos). */
const INTEGER_DATATYPES = new Set([
  'IFCINTEGER',
  'IFCPOSITIVEINTEGER',
  'IFCBINARY',
])

/** Um literal é um inteiro XSD válido (sem ponto decimal)? */
function isXsdInteger(s: string): boolean {
  return /^[+-]?\d+$/.test(s.trim())
}

/** Valida a coerência semântica de um valor face ao dataType declarado.
 *  Lança quando o IDS é semanticamente inválido (um validador estrito
 *  rejeita-o), à imagem do que a suite oficial marca como `invalid-`.
 *  Hoje: valores decimais em datatypes inteiros. */
function validateFacetSemantics(f: Facet, m: IdsMessages): void {
  if (f.type !== 'property' || f.dataType === undefined) return
  if (!INTEGER_DATATYPES.has(f.dataType.toUpperCase())) return
  // Fixado numa constante: dentro da closure o TypeScript já não sabe
  // que o `return` acima garantiu que não é undefined.
  const dataType = f.dataType
  const check = (v: IdsValue | undefined) => {
    if (!v) return
    if (v.simpleValue !== undefined && !isXsdInteger(v.simpleValue)) {
      throw new Error(m.integerValue(v.simpleValue, dataType))
    }
    if (v.enumeration) {
      for (const e of v.enumeration) {
        if (!isXsdInteger(e)) {
          throw new Error(m.integerValue(e, dataType))
        }
      }
    }
  }
  check(f.value)
}

/** Valores literais a verificar num idsValue: o valor exato ou cada
 *  membro de uma enumeração. Restrições numéricas (bounds) não têm
 *  literal textual a validar aqui. */
function literalsOf(v: IdsValue | undefined): string[] {
  if (!v) return []
  if (v.simpleValue !== undefined) return [v.simpleValue]
  if (v.enumeration) return v.enumeration
  return []
}

/** Nome(s) de atributo que um facet attribute visa numa dada entidade e
 *  esquema. Nome exato -> ele próprio; xs:pattern -> os atributos
 *  primitivos da entidade cujo nome corresponde ao padrão (ancorado). */
function targetAttributeNames(
  name: IdsValue,
  entity: string,
  schema: string,
): string[] {
  if (name.simpleValue !== undefined) return [name.simpleValue]
  if (name.pattern !== undefined) {
    let re: RegExp
    try {
      re = new RegExp(`^(?:${name.pattern})$`)
    } catch {
      return []
    }
    const out: string[] = []
    for (const attr of primitiveAttributesOf(entity, schema).keys()) {
      if (re.test(attr)) out.push(attr)
    }
    return out
  }
  return []
}

/** Auditoria semântica ao nível da specification usando o esquema IFC:
 *  um facet attribute que refere um atributo cujo tipo-base declarado é
 *  incompatível com o valor/restrição do IDS torna a spec insatisfazível
 *  (o mesmo que a suite oficial marca como `invalid-`). Regras:
 *   - valor com parte decimal num atributo inteiro (ex.: 42.0 em
 *     IfcStairFlight.NumberOfRisers);
 *   - xs:pattern num atributo não-textual (ex.: RefractionIndex, real).
 *  Só actua quando a entidade e o atributo resolvem no esquema; caso
 *  contrário fica em silêncio (tolerante), para não rejeitar IDS válidos
 *  sobre entidades/atributos que não conhecemos. */
function validateSpecAttributeSemantics(spec: IdsSpec, m: IdsMessages): void {
  const entities: string[] = []
  for (const f of spec.applicability) {
    if (f.type === 'entity' && f.name.simpleValue !== undefined) {
      entities.push(f.name.simpleValue)
    }
  }
  if (entities.length === 0) return
  const schemas = spec.ifcVersion.length > 0 ? spec.ifcVersion : ['IFC4']

  const attrFacets = [...spec.applicability, ...spec.requirements].filter(
    (f): f is AttributeFacet => f.type === 'attribute',
  )
  for (const af of attrFacets) {
    for (const entity of entities) {
      for (const schema of schemas) {
        const names = targetAttributeNames(af.name, entity, schema)
        for (const attrName of names) {
          const cat: AttrBaseType | undefined = attributeBaseType(
            entity,
            attrName,
            schema,
          )
          if (cat === undefined) continue
          if (cat === 'integer') {
            for (const lit of literalsOf(af.value)) {
              if (!isXsdInteger(lit)) {
                throw new Error(
                  m.integerAttribute(lit, entity, attrName, schema),
                )
              }
            }
          }
          if (af.value?.pattern !== undefined && !acceptsPattern(cat)) {
            throw new Error(
              m.patternNotText(entity, attrName, schema, cat),
            )
          }
        }
      }
    }
  }
}

/** Faz o parse de um ficheiro IDS (texto XML). Lança Error com mensagem
 *  em PT quando o XML é inválido, não é um IDS, ou é semanticamente
 *  inválido (ex.: valor decimal num dataType inteiro). */
export function parseIds(xml: string, options: ParseIdsOptions = {}): IdsFile {
  const m = options.messages ?? MESSAGES_EN
  let doc
  try {
    doc = options.domParser
      ? parseXmlWith(options.domParser, xml)
      : parseXml(xml)
  } catch (e) {
    if (e instanceof XmlParseError) {
      // O `cause` leva o erro do parser: a mensagem que sai é nossa e
      // está traduzida, mas quem abrir a consola tem de conseguir
      // chegar ao que o XML dizia.
      throw new Error(m.xmlUnreadable(e.message), { cause: e })
    }
    throw e
  }
  const root = doc.documentElement
  if (!root || root.localName !== 'ids') {
    throw new Error(m.notAnIds)
  }
  const specsContainer = kid(root, 'specifications')
  const specs = specsContainer
    ? kids(specsContainer, 'specification').map(parseSpec)
    : []
  for (const spec of specs) {
    for (const f of spec.applicability) validateFacetSemantics(f, m)
    for (const f of spec.requirements) validateFacetSemantics(f, m)
    validateSpecAttributeSemantics(spec, m)
  }
  return {
    info: parseInfo(kid(root, 'info')),
    specs,
  }
}

/** Conta os facets não suportados numa spec (para o relatório avisar). */
export function countUnsupported(spec: IdsSpec): number {
  const isUns = (f: Facet) => f.type === 'unsupported'
  return spec.applicability.filter(isUns).length +
    spec.requirements.filter(isUns).length
}
